import type { Humeur } from './Visage'

/**
 * Ce que le visage dit, et pourquoi — RG-2.40.
 *
 * Le catalogue complet des messages, de leurs expressions et de leurs
 * déclencheurs est écrit dans `docs/guides/visage-messages.md` : ce fichier en
 * est l'implémentation, pas la spécification.
 *
 * **Trois garanties, et elles gouvernent tout le reste.**
 *
 *   · **Il ne ment jamais sur la performance.** Féliciter une série d'échecs
 *     détruit la confiance qu'on a dans tout ce qu'il dit ensuite — y compris
 *     ses conseils, qui sont sa seule utilité réelle.
 *   · **Il commente le geste, jamais la personne.** « Celle-là est difficile »
 *     et non « tu es faible en botanique ». Un candidat se trompe des dizaines
 *     de fois par séance.
 *   · **Il se tait quand il n'a rien à dire.** Un compagnon qui commente
 *     chaque réponse devient un bruit qu'on apprend à ignorer, et le jour où
 *     il a quelque chose d'utile, on ne le lit plus.
 *
 * **Les règles sont ORDONNÉES, la première qui s'applique gagne.** L'ordre est
 * la décision : ce qui se passe maintenant prime sur une belle série passée.
 *
 * **Le contexte change le ton, pas les faits.** Réviser ses marque-pages n'est
 * pas la même activité que défiler le Fil : dans « à revoir », se tromper est
 * NORMAL — ce sont des questions déjà ratées — et le dire sur le ton du Fil
 * ferait d'une séance de rattrapage une séance d'échecs.
 */

export type Contexte = 'feed' | 'bookmarks' | 'review'

export interface Trace {
  /** `null` quand la clé est incertaine : elle ne tranche pas (RG-04-40). */
  juste: boolean | null
  /** Le chemin `ltree` du thème principal, pour nommer ce qui coince. */
  theme: string
  /** Le libellé lisible du thème — « Cellule végétale ». */
  libelle: string
  /** A-t-elle déjà été ratée avant cette séance ? */
  dejaRatee: boolean
  /** Est-elle déjà dans les marque-pages du candidat ? */
  marquee: boolean
}

export interface Reaction {
  humeur: Humeur
  message: string
  /** Ce qu'on propose de faire — l'écran en fait un bouton, ou rien. */
  conseil?: { texte: string; geste: 'lecon' | 'marquer' | 'quiz-du-theme' } | undefined
}

/** Ce qu'il faut d'échecs consécutifs pour qu'on change de ton. */
const SERIE_NOIRE = 3

/** Ce qu'il faut de réussites consécutives pour qu'on le dise. */
const BELLE_SERIE = 5

/** Au-delà, la séance est longue : on parle d'endurance, plus de score. */
const SEANCE_LONGUE = 15

export function reagir(traces: Trace[], contexte: Contexte = 'feed'): Reaction {
  const tranchees = traces.filter((t): t is Trace & { juste: boolean } => t.juste !== null)

  if (tranchees.length === 0) {
    return ouverture(contexte)
  }

  const derniere = tranchees[tranchees.length - 1]
  const echecs = finDeSerie(tranchees, false)
  const reussites = finDeSerie(tranchees, true)

  // ── 1. La série noire prime sur tout ────────────────────────────────────
  //
  // C'est elle qui fait fermer l'application, et le seul moment où un conseil
  // vaut mieux qu'un encouragement.
  if (echecs >= SERIE_NOIRE) {
    const coince = themeQuiCoince(tranchees.slice(-6))

    if (coince !== null) {
      return {
        humeur: 'pensif',
        message:
          contexte === 'review'
            ? `${String(echecs)} de suite sur ${coince.libelle}. Ces questions-là résistent depuis un moment : la leçon ira plus vite que la répétition.`
            : `${String(echecs)} de suite sur ${coince.libelle}. Le thème n’est pas acquis — la leçon ira plus vite que le fil.`,
        conseil: { texte: 'Ouvrir la leçon', geste: 'lecon' },
      }
    }

    if (contexte === 'review') {
      return {
        humeur: 'encourageant',
        message: `${String(echecs)} de suite — mais ce sont vos questions difficiles, c’est pour cela qu’elles sont là. Continuez.`,
      }
    }

    if (derniere !== undefined && !derniere.marquee) {
      return {
        humeur: 'pensif',
        message: `${String(echecs)} de suite. Ce n’est pas le bon moment : marquez celles qui résistent, on y reviendra.`,
        conseil: { texte: 'Marquer celle-ci', geste: 'marquer' },
      }
    }

    return {
      humeur: 'pensif',
      message: `${String(echecs)} de suite. Faites une pause, ou changez de thème — insister maintenant coûte plus que ça ne rapporte.`,
    }
  }

  // ── 2. Une question déjà ratée qui retombe ──────────────────────────────
  //
  // Le signal le plus utile du lot : elle reviendra encore, et la marquer coûte
  // une tape aujourd'hui contre trois échecs demain.
  if (derniere !== undefined && !derniere.juste && derniere.dejaRatee) {
    if (derniere.marquee) {
      return {
        humeur: 'pensif',
        message: 'Encore elle. Elle est déjà marquée — la leçon la traite mieux que la répétition.',
        conseil: { texte: 'Ouvrir la leçon', geste: 'lecon' },
      }
    }

    return {
      humeur: 'pensif',
      message: 'Celle-là vous avait déjà échappé. Gardez-la sous la main.',
      conseil: { texte: 'Marquer celle-ci', geste: 'marquer' },
    }
  }

  // ── 3. Une question déjà ratée, cette fois réussie ──────────────────────
  //
  // Le seul moment où l'on peut féliciter quelque chose de mesurable : ce n'est
  // pas de la chance, c'est un progrès daté.
  if (derniere !== undefined && derniere.juste && derniere.dejaRatee) {
    return {
      humeur: 'ravi',
      message: 'Celle-là vous résistait. Plus maintenant.',
    }
  }

  // ── 4. La belle série ───────────────────────────────────────────────────
  if (reussites >= BELLE_SERIE) {
    if (contexte === 'review') {
      return {
        humeur: 'ravi',
        message: `${String(reussites)} d’affilée sur vos questions difficiles. Elles ne le sont plus vraiment.`,
      }
    }

    if (contexte === 'bookmarks') {
      return {
        humeur: 'ravi',
        message: `${String(reussites)} d’affilée. Ces marque-pages ont fait leur travail — vous pouvez en retirer.`,
      }
    }

    return {
      humeur: 'ravi',
      message: `${String(reussites)} d’affilée. C’est le moment de pousser un peu.`,
      conseil: { texte: 'Un quiz sur ce thème', geste: 'quiz-du-theme' },
    }
  }

  if (reussites >= 3) {
    return { humeur: 'content', message: `${String(reussites)} de suite. Le thème tient.` }
  }

  if (reussites === 2) {
    return { humeur: 'content', message: 'Deux de suite. Ça tient.' }
  }

  // ── 5. La séance longue ─────────────────────────────────────────────────
  //
  // On félicite la RÉGULARITÉ, pas le score : c'est elle qui fait réussir, et
  // c'est la seule chose que le candidat contrôle entièrement.
  if (tranchees.length >= SEANCE_LONGUE) {
    const justes = tranchees.filter((t) => t.juste).length
    const part = justes / tranchees.length

    if (part >= 0.7) {
      return {
        humeur: 'ravi',
        message: `${String(tranchees.length)} questions, et ça tient. C’est une vraie séance.`,
      }
    }

    return {
      humeur: 'encourageant',
      message: `${String(tranchees.length)} questions aujourd’hui. Le score compte moins que le fait d’être là.`,
    }
  }

  // ── 6. Un échec isolé ───────────────────────────────────────────────────
  //
  // Le cas le plus fréquent, et celui où il faut en dire le moins : une seule
  // erreur ne veut rien dire, et en faire un événement apprendrait à la
  // craindre.
  if (derniere !== undefined && !derniere.juste) {
    if (contexte === 'review') {
      return { humeur: 'neutre', message: 'Elle résiste encore. C’est normal, elle est là pour ça.' }
    }

    if (contexte === 'bookmarks') {
      return { humeur: 'encourageant', message: 'Vous l’aviez mise de côté à raison. On y revient.' }
    }

    return { humeur: 'encourageant', message: 'Celle-là est difficile. On continue.' }
  }

  // ── 7. Une réussite ordinaire ───────────────────────────────────────────
  if (contexte === 'bookmarks') {
    return { humeur: 'content', message: 'Acquise. Ce marque-page a servi.' }
  }

  if (contexte === 'review') {
    return { humeur: 'content', message: 'Retrouvée. Elle s’éloignera dans le temps.' }
  }

  return { humeur: 'content', message: 'Bien vu. La suivante.' }
}

/** Le premier mot d'une séance, avant toute réponse. */
function ouverture(contexte: Contexte): Reaction {
  if (contexte === 'bookmarks') {
    return { humeur: 'neutre', message: 'Vos questions mises de côté. On les reprend ?' }
  }

  if (contexte === 'review') {
    return { humeur: 'neutre', message: 'Celles qui vous ont résisté. Une à la fois.' }
  }

  return { humeur: 'neutre', message: 'On y va ? Une question à la fois.' }
}

/** La longueur de la série de fin, pour une valeur donnée. */
function finDeSerie(traces: (Trace & { juste: boolean })[], valeur: boolean): number {
  let compte = 0

  for (let i = traces.length - 1; i >= 0; i--) {
    if (traces[i]?.juste !== valeur) break

    compte++
  }

  return compte
}

/**
 * Le thème sur lequel les échecs se concentrent, s'il y en a un.
 *
 * **Il faut au moins trois échecs sur le MÊME thème.** Deux ratés sur deux
 * thèmes différents ne disent rien : c'est une mauvaise passe, pas une lacune.
 * Nommer un thème à tort enverrait relire une leçon de vingt minutes pour rien.
 */
function themeQuiCoince(
  recentes: (Trace & { juste: boolean })[],
): { theme: string; libelle: string } | null {
  const rates = recentes.filter((t) => !t.juste)
  const compte = new Map<string, { libelle: string; combien: number }>()

  for (const trace of rates) {
    const deja = compte.get(trace.theme)

    compte.set(trace.theme, {
      libelle: trace.libelle,
      combien: (deja?.combien ?? 0) + 1,
    })
  }

  for (const [theme, { libelle, combien }] of compte) {
    if (combien >= 3) return { theme, libelle }
  }

  return null
}
